import { PaneArea, ResizeByThumbWithTypeAndDifs } from '../../types/shared'
import twColors from '../constants/colors'
import { CanvasElementType, TextAlign, TextBaseline } from '../constants/common'
import { getRoundedObject } from '../controllers/renderUtils'
import PaneModel from '../models/pane'
import { numberToStringWithUnitsSuffix } from '../utils'
import { convertColorToHEX, getHEXDarkenedColor, getHEXWithSpecificAplha } from '../utils/colors'
import { getRoundedString, isPointInBox } from '../utils/helpers'
import Element from './element'
import Text, { ITextAttrs } from './text'
import Thumb from './thumb'

interface Coordinates {
  x1: number
  x2: number
  y1: number
  y2: number
}

export interface IMeasureAttrs extends Coordinates {
  positiveColor: string
  negativeColor: string
  lineWidth: number
}

class Measure<Attrs extends IMeasureAttrs = IMeasureAttrs> extends Element<Attrs> {
  static type = CanvasElementType.measure

  name = 'Measure'
  declare scaled: Pick<Attrs, 'x1' | 'x2' | 'y1' | 'y2'>

  x1Dif: number
  x2Dif: number
  startingX1: number
  startingX2: number
  labelBox = { x: 0, y: 0, width: 0, height: 0 }

  constructor(values: Partial<Attrs>, model: PaneModel) {
    super(values, model)
    this.resize = this.resize.bind(this)
    this._thumbs = [
      new Thumb(
        'tl',
        () => this.attrs.x1,
        () => this.attrs.y1,
        this.resize,
        this.model
      ),
      new Thumb(
        'br',
        () => this.attrs.x2,
        () => this.attrs.y2,
        this.resize,
        this.model
      ),
    ]
    this.scale(this.getBoundingPointKeys())
    this.x1Dif = this.x2Dif = 0

    this.startingX1 = this.attrs.x1
    this.startingX2 = this.attrs.x2
  }

  getDefaults() {
    const { ElementSettings } = this.getChartLayoutSettings()
    return {
      positiveColor: ElementSettings.defaultFill,
      negativeColor: getHEXWithSpecificAplha(twColors.red[400], 0.15),
      lineWidth: 1,
    } as Partial<Attrs>
  }

  getBoundingPointKeys = () => ({ x: ['x1', 'x2'], y: ['y1', 'y2'] })

  getVolumeSumString = () => {
    const { volume, barIndex }: { volume: number[]; barIndex: number[] } = this.model.chart().quote()
    const roundedX1 = Math.round(this.attrs.x1)
    const roundedX2 = Math.round(this.attrs.x2)
    // min/max values are offset by 1 so there's no value on such index
    // and at the same time it won't iterate over unlimited number of nonexisting indexes
    const minIndexLimit = barIndex[0] - 1
    const maxIndexLimit = barIndex[barIndex.length - 1] + 1
    const volMinIndex = Math.min(Math.max(Math.min(roundedX1, roundedX2), minIndexLimit), maxIndexLimit)
    const volMaxIndex = Math.min(Math.max(Math.max(roundedX1, roundedX2), minIndexLimit), maxIndexLimit)

    let volumeSum = 0
    for (let i = volMinIndex; i <= volMaxIndex; i++) {
      const volumeIndex = barIndex.indexOf(i)
      if (volumeIndex >= 0) {
        volumeSum += volume[volumeIndex]
      }
    }

    return `Vol ${numberToStringWithUnitsSuffix(volumeSum)}`
  }

  renderContent(context: CanvasRenderingContext2D) {
    const { x1, x2, y1, y2 } = this.scaled
    const roundedXY = getRoundedObject({ x1, x2, y1, y2 })

    const width = Math.abs(roundedXY.x2 - roundedXY.x1)
    const height = Math.abs(roundedXY.y2 - roundedXY.y1)

    const priceDif = this.attrs.y2 - this.attrs.y1
    const isNegative = priceDif < 0

    const fillColor = isNegative ? this.attrs.negativeColor : this.attrs.positiveColor
    const lineColor = convertColorToHEX(fillColor, false)

    // Determine visual boundaries of the rectangle
    const visualTop = Math.min(roundedXY.y1, roundedXY.y2)
    const visualBottom = Math.max(roundedXY.y1, roundedXY.y2)
    const visualLeft = Math.min(roundedXY.x1, roundedXY.x2)
    const visualRight = Math.max(roundedXY.x1, roundedXY.x2)

    // Draw background
    context.set('fillStyle', fillColor)
    context.fillRect(visualLeft, visualTop, width, height)

    // Draw cross lines (vertical and horizontal)
    context.set('strokeStyle', lineColor)
    context.set('lineWidth', this.attrs.lineWidth)
    context.beginPath()

    // Vertical line in the middle
    const centerX = visualLeft + width / 2
    context.moveTo(centerX, visualTop)
    context.lineTo(centerX, visualBottom)

    // Horizontal line in the middle
    const centerY = visualTop + height / 2
    context.moveTo(visualLeft, centerY)
    context.lineTo(visualRight, centerY)
    context.stroke()

    const percentage = (100 / this.attrs.y1) * priceDif
    const heightStr = `${priceDif.toFixed(2)} (${percentage.toFixed(2)}%)`

    // Prepare all three label texts
    const barsText = `${getRoundedString({ number: Math.abs(this.attrs.x2 - this.attrs.x1), numOfDecimalPlaces: 2 })} bars`
    const volumeText = this.getVolumeSumString()

    // Create temporary text objects to measure widths
    const text = new Text(
      {
        text: barsText,
        fillStyle: 'white',
        font: { size: 9, family: 'Finviz Sans, Inter Fallback', weight: '500' },
        lineHeight: 16,
        textBaseline: TextBaseline.middle,
        textAlign: TextAlign.center,
      },
      this.model
    )

    // Measure all texts to find the widest one
    const barsWidth = text.measure(context)
    text.set({ text: heightStr })
    const heightWidth = text.measure(context)
    text.set({ text: volumeText })
    const volumeWidth = text.measure(context)

    const maxTextWidth = Math.max(barsWidth, heightWidth, volumeWidth)
    const lineHeight = text.attrs.lineHeight
    const paddingX = 8
    const paddingTop = 4
    const paddingBottom = 2

    // Calculate total label box dimensions
    const totalBoxWidth = maxTextWidth + paddingX * 2
    const totalBoxHeight = lineHeight * 3 + paddingTop + paddingBottom

    // Calculate position (above for positive/blue, below for negative/red)
    const baseX = centerX
    const gap = 8
    const boxY = isNegative ? visualBottom + gap : visualTop - totalBoxHeight - gap
    const boxX = baseX - totalBoxWidth / 2

    // Store label box dimensions for hit detection
    this.labelBox = { x: boxX, y: boxY, width: totalBoxWidth, height: totalBoxHeight }

    // Draw single background box for all labels
    const labelBackgroundColor = getHEXDarkenedColor(fillColor)
    context.set('fillStyle', labelBackgroundColor)
    context.fillRect(boxX, boxY, totalBoxWidth, totalBoxHeight)

    text.set({
      text: barsText,
      background: undefined,
      x: baseX,
      y: boxY + paddingTop + lineHeight * 0.5,
    } as Partial<ITextAttrs>)
    text.render(context)

    text.set({
      text: heightStr,
      x: baseX,
      y: boxY + paddingTop + lineHeight * 1.5,
    } as Partial<ITextAttrs>)
    text.render(context)

    text.set({
      text: volumeText,
      x: baseX,
      y: boxY + paddingTop + lineHeight * 2.5,
    } as Partial<ITextAttrs>)
    text.render(context)

    if (this.getShouldRenderThumbs()) {
      this.renderThumbs(context)
    }
  }

  addToCoordinates(difs: Partial<Coordinates>) {
    this.attrs.y1 += difs.y1 || 0
    this.attrs.y2 += difs.y2 || 0

    this.x1Dif += difs.x1 || 0
    this.x2Dif += difs.x2 || 0

    // The reason for adding +1 is to "ceil" the number when moving to the left (decreasing bar index)
    const newX1 = ~~(this.startingX1 + this.x1Dif) + (this.x1Dif < 0 ? 1 : 0)
    if (~~this.attrs.x1 - newX1 !== 0) {
      this.attrs.x1 = newX1
    }
    const newX2 = ~~(this.startingX2 + this.x2Dif) + (this.x2Dif < 0 ? 1 : 0)
    if (~~this.attrs.x2 - newX2 !== 0) {
      this.attrs.x2 = newX2
    }
  }

  moveBy(x: number, y: number) {
    return this.addToCoordinates({
      x1: x,
      y1: y,
      x2: x,
      y2: y,
    })
  }

  resize({ type, difX, difY }: ResizeByThumbWithTypeAndDifs) {
    if (type === 'tl') {
      this.addToCoordinates({ x1: difX, y1: difY })
    } else {
      this.addToCoordinates({ x2: difX, y2: difY })
    }
  }

  isInArea(area: PaneArea) {
    if (super.isDrawingElementLockedOrInvisible()) return false

    // Check main rectangle in unscaled coordinates
    const x1 = Math.round(this.attrs.x1)
    const x2 = Math.round(this.attrs.x2)
    const left = Math.min(x1, x2)
    const right = Math.max(x1, x2)
    const rectTop = Math.min(this.attrs.y1, this.attrs.y2)
    const rectBottom = Math.max(this.attrs.y1, this.attrs.y2)

    if (left < area.x && area.x < right && rectTop < area.y && area.y < rectBottom) {
      return true
    }

    // Check label box using stored dimensions from renderContent
    if (isPointInBox(area.scaled, this.labelBox)) {
      return true
    }

    return super.isInArea(area)
  }

  onMouseDown(paneArea: PaneArea) {
    super.onMouseDown(paneArea)
    this.startingX1 = this.attrs.x1
    this.startingX2 = this.attrs.x2
  }

  onMouseUp(paneArea?: PaneArea) {
    super.onMouseUp(paneArea)
    this.x1Dif = this.x2Dif = 0
  }
}

Measure.prototype.modalConfig = {
  inputs: [
    { type: 'background', name: 'positiveColor', label: 'Positive' },
    { type: 'background', name: 'negativeColor', label: 'Negative' },
    { type: 'range', name: 'lineWidth', label: 'Line width', min: 1, max: 10 },
  ],
}
export default Measure
